<template>
  <LinkElement
    v-if="hasDescription"
    link="javascript:;"
    style-type="default"
    :data-testid="testId"
    @click.prevent="open"
  >
    {{ label }}
  </LinkElement>
  <ModalModule
    v-if="hasDescription"
    v-model="isOpen"
    :title="title"
    :close-icon-text="closeIconText"
    :adaptive-width="true"
    :esc-to-close="true"
    :click-to-close="true"
    @abort="isOpen = false"
  >
    <DictionaryRichText :dictionary-key="descriptionKey" />
  </ModalModule>
</template>

<script setup lang="ts">
import { LinkElement, ModalModule } from '@hypercodestudio/basler-components';

import { useLightingAdvisorAnalytics } from '~/composables/useLightingAdvisorAnalytics';
import { dictText } from '~/utils/lightingAdvisor/questionsCatalog';

interface Props {
  stepNumber: number;
  testId?: string;
}

const props = withDefaults(defineProps<Props>(), {
  testId: 'lighting-advisor-step-explanation'
});

const { $textDictionary } = useNuxtApp();
const analytics = useLightingAdvisorAnalytics();

const isOpen = ref(false);

const dictionary = computed(
  () => ($textDictionary.value ?? {}) as Record<string, string | undefined>
);

const descriptionKey = computed(
  () => `lightingAdvisor.step.${props.stepNumber}.description`
);

const descriptionDocuments = useRichTextDictionary(
  computed(() => [descriptionKey.value])
);

const hasDescription = computed(() =>
  Boolean(descriptionDocuments.value[descriptionKey.value])
);

const title = computed(() =>
  dictText(dictionary.value, `lightingAdvisor.step.${props.stepNumber}.title`)
);

const label = computed(
  () =>
    dictionary.value['lightingAdvisor.step.explanationLink'] ??
    'Get an explanation for this question'
);

const closeIconText = computed(
  () => dictionary.value['productFinder.modal.common.closeIconText'] ?? 'close'
);

function open(): void {
  if (!hasDescription.value) {
    return;
  }
  isOpen.value = true;
  analytics.trackStepExplanation(title.value);
}
</script>
